import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck, BarChart3, Megaphone, X } from "lucide-react";
import {
  getConsent,
  hasDecision,
  saveConsent,
  acceptAll,
  rejectAll,
  OPEN_PREFS_EVENT,
} from "../../lib/consent";

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      data-testid={testid}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? "bg-brand-orange" : "bg-gray-300"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const bannerRef = useRef(null);

  // Reserve space at the bottom of the page while the banner is open so it
  // never covers the footer (incl. legal links) on mobile.
  useEffect(() => {
    const open = showBanner && !prefsOpen;
    const apply = () => {
      document.body.style.paddingBottom = open && bannerRef.current
        ? `${bannerRef.current.offsetHeight + 24}px`
        : "";
    };
    apply();
    if (open) window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      document.body.style.paddingBottom = "";
    };
  }, [showBanner, prefsOpen]);

  useEffect(() => {
    setShowBanner(!hasDecision());
    const openPrefs = () => {
      const c = getConsent();
      setAnalytics(!!c?.analytics);
      setMarketing(!!c?.marketing);
      setPrefsOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, openPrefs);
  }, []);

  useEffect(() => {
    if (!prefsOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setPrefsOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prefsOpen]);

  const finish = () => {
    setShowBanner(false);
    setPrefsOpen(false);
  };
  const onAcceptAll = () => { acceptAll(); finish(); };
  const onRejectAll = () => { rejectAll(); finish(); };
  const onSavePrefs = () => { saveConsent({ analytics, marketing }); finish(); };
  const openPrefsFromBanner = () => {
    const c = getConsent();
    setAnalytics(!!c?.analytics);
    setMarketing(!!c?.marketing);
    setPrefsOpen(true);
  };

  return (
    <>
      {/* Bottom banner */}
      <AnimatePresence>
        {showBanner && !prefsOpen && (
          <motion.div
            ref={bannerRef}
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cookie-banner"
            className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-5 sm:pb-5"
          >
            <div className="mx-auto max-w-4xl rounded-2xl border border-gray-200 bg-white/95 backdrop-blur-xl shadow-[0_20px_50px_-15px_rgba(22,58,95,0.35)] p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                  <Cookie size={18} />
                </span>
                <div className="text-sm leading-relaxed text-ink/75">
                  <p className="font-display font-bold text-navy text-base">Rispettiamo la tua privacy</p>
                  <p className="mt-1">
                    Usiamo cookie tecnici necessari e, solo con il tuo consenso, cookie di
                    analisi e di marketing (Google Analytics, Google Ads, Meta Pixel) per
                    migliorare il sito e misurare le campagne. Puoi accettare, rifiutare o
                    scegliere le tue preferenze. Maggiori dettagli nella{" "}
                    <Link to="/cookie-policy" className="font-semibold text-navy underline underline-offset-2 hover:text-brand-orange">Cookie Policy</Link>{" "}
                    e nella{" "}
                    <Link to="/privacy" className="font-semibold text-navy underline underline-offset-2 hover:text-brand-orange">Privacy Policy</Link>.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-col sm:flex-row sm:justify-end gap-2.5">
                <button
                  onClick={onRejectAll}
                  data-testid="cookie-reject-all"
                  className="order-2 sm:order-1 rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors"
                >
                  Rifiuta tutti
                </button>
                <button
                  onClick={openPrefsFromBanner}
                  data-testid="cookie-customize"
                  className="order-3 sm:order-2 rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors"
                >
                  Personalizza
                </button>
                <button
                  onClick={onAcceptAll}
                  data-testid="cookie-accept-all"
                  className="order-1 sm:order-3 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_-8px_rgba(242,140,40,0.8)] hover:bg-brand-orange-dark transition-colors"
                >
                  Accetta tutti
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences modal */}
      <AnimatePresence>
        {prefsOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setPrefsOpen(false)}
              className="fixed inset-0 z-50 bg-black/50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 20 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              data-testid="cookie-prefs-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-prefs-title"
              className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-1.5rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-2xl overflow-hidden"
            >
              <div className="bg-navy px-6 py-5 text-white relative">
                <h2 id="cookie-prefs-title" className="font-display font-extrabold text-lg flex items-center gap-2">
                  <Cookie size={20} /> Preferenze cookie
                </h2>
                <p className="mt-1 text-sm text-white/70">Scegli quali categorie di cookie autorizzare.</p>
                <button
                  onClick={() => setPrefsOpen(false)}
                  data-testid="cookie-prefs-close"
                  aria-label="Chiudi"
                  className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-white/80 hover:bg-white/15 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="max-h-[55vh] overflow-y-auto px-6 py-5 space-y-4">
                <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4">
                  <div className="flex gap-3">
                    <ShieldCheck size={18} className="mt-0.5 shrink-0 text-navy" />
                    <div>
                      <p className="font-semibold text-navy text-sm">Necessari</p>
                      <p className="mt-0.5 text-xs text-ink/60">Indispensabili al funzionamento del sito (es. memorizzazione del consenso). Sempre attivi.</p>
                    </div>
                  </div>
                  <Toggle checked disabled testid="cookie-toggle-necessary" onChange={() => {}} />
                </div>

                <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4">
                  <div className="flex gap-3">
                    <BarChart3 size={18} className="mt-0.5 shrink-0 text-navy" />
                    <div>
                      <p className="font-semibold text-navy text-sm">Analitici</p>
                      <p className="mt-0.5 text-xs text-ink/60">Statistiche aggregate sull'uso del sito (Google Analytics). Ci aiutano a migliorare.</p>
                    </div>
                  </div>
                  <Toggle checked={analytics} onChange={setAnalytics} testid="cookie-toggle-analytics" />
                </div>

                <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4">
                  <div className="flex gap-3">
                    <Megaphone size={18} className="mt-0.5 shrink-0 text-navy" />
                    <div>
                      <p className="font-semibold text-navy text-sm">Marketing</p>
                      <p className="mt-0.5 text-xs text-ink/60">Misurazione delle campagne e remarketing (Google Ads, Meta Pixel).</p>
                    </div>
                  </div>
                  <Toggle checked={marketing} onChange={setMarketing} testid="cookie-toggle-marketing" />
                </div>
              </div>

              <div className="border-t border-gray-100 px-6 py-4 flex flex-col sm:flex-row sm:justify-end gap-2.5">
                <button onClick={onRejectAll} data-testid="cookie-prefs-reject-all" className="rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
                  Rifiuta tutti
                </button>
                <button onClick={onSavePrefs} data-testid="cookie-save-prefs" className="rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors">
                  Salva preferenze
                </button>
                <button onClick={onAcceptAll} data-testid="cookie-prefs-accept-all" className="rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-orange-dark transition-colors">
                  Accetta tutti
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
